import React, { createContext, useContext, useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";

import { CallsService } from "../../services/calls.service";
import { Agent, CallFilters } from "../../types/calls.types";

const EMPTY_FILTERS: CallFilters = {};

// Every CallFilters key that's safe to round-trip through the URL as a
// plain string (i.e. all of them - CallFilters has no non-string fields).
const FILTER_KEYS = [
  "tenant",
  "status",
  "direction",
  "calling_name",
  "extension",
  "caller_id",
  "called_number",
  "start_time",
  "end_time",
] as const satisfies readonly (keyof CallFilters)[];

const filtersFromSearchParams = (params: URLSearchParams): CallFilters => {
  const result: CallFilters = {};
  FILTER_KEYS.forEach((key) => {
    const value = params.get(key);
    if (value) (result as Record<string, string>)[key] = value;
  });
  return result;
};

const searchParamsFromFilters = (filters: CallFilters): URLSearchParams => {
  const params = new URLSearchParams();
  FILTER_KEYS.forEach((key) => {
    const value = filters[key];
    if (value !== undefined && value !== "") {
      params.set(key, value);
    }
  });
  return params;
};

export type RelatedCallsDimension = "calling_name" | "called_number" | "caller_id";

interface RelatedCallsTarget {
  dimension: RelatedCallsDimension;
  value: string;
  label: string;
}

interface CallsExplorerContextType {
  filters: CallFilters;
  pendingFilters: CallFilters;
  setPendingFilter: <K extends keyof CallFilters>(key: K, value: CallFilters[K]) => void;
  applyFilters: () => void;
  clearFilters: () => void;

  agents: Agent[];
  agentsLoading: boolean;

  // Broadcast-style signal: CallRow is deep in the tree and needs to open a
  // modal that renders once at the page root (RelatedCallsModal), so this
  // avoids prop-drilling an open/close handler down through CallRegistry.
  relatedCallsTarget: RelatedCallsTarget | null;
  openRelatedCalls: (dimension: RelatedCallsDimension, value: string, label: string) => void;
  closeRelatedCalls: () => void;
  // Promotes a related-calls preview to the main list: applies it as the
  // page's sole filter (replacing whatever was active) and closes the modal.
  viewInMainList: (dimension: RelatedCallsDimension, value: string) => void;
}

const CallsExplorerContext = createContext<CallsExplorerContextType | null>(null);

export const CallsExplorerProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  // Applied filters are mirrored to the URL query string so a refresh (or a
  // shared link) doesn't lose them - initialise from whatever's already
  // there on mount.
  const [searchParams, setSearchParams] = useSearchParams();

  // "Applied" filters drive the fetch; "pending" filters track the form
  // while it's being edited, so typing/selecting doesn't refetch until the
  // user clicks Apply.
  const [filters, setFilters] = useState<CallFilters>(() => filtersFromSearchParams(searchParams));
  const [pendingFilters, setPendingFilters] = useState<CallFilters>(() =>
    filtersFromSearchParams(searchParams),
  );

  const [agents, setAgents] = useState<Agent[]>([]);
  const [agentsLoading, setAgentsLoading] = useState(false);

  const [relatedCallsTarget, setRelatedCallsTarget] = useState<RelatedCallsTarget | null>(null);

  useEffect(() => {
    const fetchAgents = async () => {
      setAgentsLoading(true);
      try {
        const data = await CallsService.listAgents(true);
        setAgents(data);
      } catch (err) {
        console.error("Failed to fetch agents:", err);
      } finally {
        setAgentsLoading(false);
      }
    };
    void fetchAgents();
  }, []);

  const setPendingFilter = <K extends keyof CallFilters>(key: K, value: CallFilters[K]) => {
    setPendingFilters((prev) => ({ ...prev, [key]: value }));
  };

  const applyFilters = () => {
    setFilters(pendingFilters);
    setSearchParams(searchParamsFromFilters(pendingFilters), { replace: true });
  };

  const clearFilters = () => {
    setPendingFilters(EMPTY_FILTERS);
    setFilters(EMPTY_FILTERS);
    setSearchParams(new URLSearchParams(), { replace: true });
  };

  const openRelatedCalls = (dimension: RelatedCallsDimension, value: string, label: string) => {
    setRelatedCallsTarget({ dimension, value, label });
  };

  const closeRelatedCalls = () => {
    setRelatedCallsTarget(null);
  };

  const viewInMainList = (dimension: RelatedCallsDimension, value: string) => {
    const next: CallFilters = { [dimension]: value };
    setPendingFilters(next);
    setFilters(next);
    setSearchParams(searchParamsFromFilters(next), { replace: true });
    setRelatedCallsTarget(null);
  };

  return (
    <CallsExplorerContext.Provider
      value={{
        filters,
        pendingFilters,
        setPendingFilter,
        applyFilters,
        clearFilters,
        agents,
        agentsLoading,
        relatedCallsTarget,
        openRelatedCalls,
        closeRelatedCalls,
        viewInMainList,
      }}
    >
      {children}
    </CallsExplorerContext.Provider>
  );
};

export const useCallsExplorer = (): CallsExplorerContextType => {
  const ctx = useContext(CallsExplorerContext);
  if (!ctx) throw new Error("useCallsExplorer must be used within CallsExplorerProvider");
  return ctx;
};
